// src/views/BackOffice/CreateRepresentatives.js
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane
} from 'reactstrap';
import axios from '../../api/Axios';

const CreateRepresentatives = () => {
  const [activeTab, setActiveTab] = useState('primary');
  const [bmos, setBmos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loadingBmos, setLoadingBmos] = useState(true);

  // Form data for Primary Representative
  const [primaryFormData, setPrimaryFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    phone_number: '',
    email_address: '',
    bmo_id: ''
  });

  // Form data for Secondary Representative
  const [secondaryFormData, setSecondaryFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    phone_number: '',
    email_address: '',
    bmo_id: ''
  });

  useEffect(() => {
    fetchBMOs();
  }, []);

  const fetchBMOs = async () => {
    try {
      setLoadingBmos(true);
      const response = await axios.get('accounts/bmo/dropdown/');
      setBmos(response.data);
    } catch (error) {
      console.error('Error fetching BMOs:', error);
      setMessage({
        type: 'danger',
        text: 'Failed to load BMO list. Please refresh the page.'
      });
    } finally {
      setLoadingBmos(false);
    }
  };

  const handlePrimaryInputChange = (e) => {
    const { name, value } = e.target;
    setPrimaryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSecondaryInputChange = (e) => {
    const { name, value } = e.target;
    setSecondaryFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const formData = type === 'primary' ? primaryFormData : secondaryFormData;
    const endpoint = type === 'primary' 
      ? 'accounts/create-primary-representative/' 
      : 'accounts/create-secondary-representative/';

    try {
      const response = await axios.post(endpoint, formData);
      
      setMessage({
        type: 'success',
        text: `${type === 'primary' ? 'Primary' : 'Secondary'} Representative "${formData.first_name} ${formData.last_name}" created successfully! An activation email has been sent.`
      });
      
      // Reset form
      if (type === 'primary') {
        setPrimaryFormData({
          first_name: '',
          last_name: '',
          title: '',
          phone_number: '',
          email_address: '',
          bmo_id: ''
        });
      } else {
        setSecondaryFormData({
          first_name: '',
          last_name: '',
          title: '',
          phone_number: '',
          email_address: '',
          bmo_id: ''
        });
      }
      
    } catch (error) {
      console.error('Representative creation error:', error);
      
      let errorMessage = `Failed to create ${type === 'primary' ? 'Primary' : 'Secondary'} Representative. Please try again.`;
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.email_address) {
        errorMessage = error.response.data.email_address[0];
      } else if (error.response?.data?.bmo_id) {
        errorMessage = error.response.data.bmo_id[0];
      }
      
      setMessage({
        type: 'danger',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const renderForm = (type, formData, handleInputChange) => (
    <Form onSubmit={(e) => handleSubmit(e, type)}>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for={`${type}_first_name`}>First Name *</Label>
            <Input
              type="text"
              name="first_name"
              id={`${type}_first_name`}
              value={formData.first_name}
              onChange={handleInputChange}
              required
              placeholder="Enter first name"
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for={`${type}_last_name`}>Last Name *</Label>
            <Input
              type="text"
              name="last_name"
              id={`${type}_last_name`}
              value={formData.last_name}
              onChange={handleInputChange}
              required
              placeholder="Enter last name"
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for={`${type}_title`}>Title</Label>
            <Input
              type="text"
              name="title"
              id={`${type}_title`}
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Mr., Ms., Dr."
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for={`${type}_phone_number`}>Phone Number *</Label>
            <Input
              type="tel"
              name="phone_number"
              id={`${type}_phone_number`}
              value={formData.phone_number}
              onChange={handleInputChange}
              required
              placeholder="Enter phone number"
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label for={`${type}_email_address`}>Email Address *</Label>
        <Input
          type="email"
          name="email_address"
          id={`${type}_email_address`}
          value={formData.email_address}
          onChange={handleInputChange}
          required
          placeholder="Enter email address"
        />
      </FormGroup>

      <FormGroup>
        <Label for={`${type}_bmo_id`}>BMO Organization *</Label>
        <Input
          type="select"
          name="bmo_id"
          id={`${type}_bmo_id`}
          value={formData.bmo_id}
          onChange={handleInputChange}
          required
          disabled={loadingBmos}
        >
          <option value="">
            {loadingBmos ? 'Loading BMOs...' : 'Select BMO Organization'}
          </option>
          {bmos.map((bmo) => (
            <option key={bmo.id} value={bmo.id}>
              {bmo.name}
            </option>
          ))}
        </Input>
      </FormGroup>

      <div className="d-flex gap-2">
        <Button
          type="submit"
          color="primary"
          disabled={loading || !formData.first_name || !formData.last_name || !formData.email_address || !formData.phone_number || !formData.bmo_id}
        >
          {loading ? 'Creating...' : `Create ${type === 'primary' ? 'Primary' : 'Secondary'} Representative`}
        </Button>
        
        <Button
          type="button"
          color="secondary"
          onClick={() => {
            if (type === 'primary') {
              setPrimaryFormData({
                first_name: '',
                last_name: '',
                title: '',
                phone_number: '',
                email_address: '',
                bmo_id: ''
              });
            } else {
              setSecondaryFormData({
                first_name: '',
                last_name: '',
                title: '',
                phone_number: '',
                email_address: '',
                bmo_id: ''
              });
            }
            setMessage({ type: '', text: '' });
          }}
        >
          Clear
        </Button>
      </div>
    </Form>
  );

  return (
    <div>
      <h2 className="mb-4">Create Representatives</h2>
      
      {message.text && (
        <Alert color={message.type} className="mb-3">
          {message.text}
        </Alert>
      )}

      <Card>
        <CardBody>
          <Nav tabs>
            <NavItem>
              <NavLink
                className={activeTab === 'primary' ? 'active' : ''}
                onClick={() => setActiveTab('primary')}
                style={{ cursor: 'pointer' }}
              >
                Primary Representative
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === 'secondary' ? 'active' : ''}
                onClick={() => setActiveTab('secondary')}
                style={{ cursor: 'pointer' }}
              >
                Secondary Representative
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="primary">
              <div className="pt-3">
                <CardTitle tag="h5">Create Primary Representative</CardTitle>
                <p className="text-muted mb-4">
                  Primary Representatives have full access to BMO management and can create BMO Officers.
                </p>
                {renderForm('primary', primaryFormData, handlePrimaryInputChange)}
              </div>
            </TabPane>
            
            <TabPane tabId="secondary">
              <div className="pt-3">
                <CardTitle tag="h5">Create Secondary Representative</CardTitle>
                <p className="text-muted mb-4">
                  Secondary Representatives assist with BMO management and can also create BMO Officers.
                </p>
                {renderForm('secondary', secondaryFormData, handleSecondaryInputChange)}
              </div>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>

      <Row className="mt-4">
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h6">Important Notes</CardTitle>
              <ul className="small">
                <li>An activation email will be sent to the representative's email address</li>
                <li>The representative must activate their account within 48 hours</li>
                <li>Both Primary and Secondary Representatives can create BMO Officers</li>
                <li>Representatives will have access to their assigned BMO's management tools</li>
                <li>Email addresses must be unique across the entire system</li>
              </ul>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CreateRepresentatives;
